import React, { useEffect, useRef } from "react";
import {
  TrustAIExecutiveService,
  type GuardianScriptedReply,
} from "@/api/temp/TrustAIExecutiveService";
import useMutation from "@/hooks/useMutation";
import useQuery from "@/hooks/useQuery";
import type { GuardianEntry, GuardianRequest } from "@/states/guardian";

/** A request stays mounted through closing, docking and route changes. Each
 * completion owns a transcript slot, never the currently selected context. */
const Request: React.FC<{
  id: string;
  request: GuardianRequest;
  complete: (id: string, reply?: GuardianScriptedReply) => void;
}> = ({ id, request, complete }) => {
  const { context, query, promptId } = request;
  const params = {
    contextKey: context.key,
    contextKind: context.kind,
    label: context.label,
    metadata: context.metadata,
  };
  const introduction = useQuery({
    method: TrustAIExecutiveService.getGuardianContextUsingGet,
    params,
    queryKey: ["getGuardianContextUsingGet", id],
    options: {
      enabled: request.kind === "introduction",
      retry: false,
      gcTime: 0,
    },
  });
  const { mutateAsync } = useMutation({
    method: TrustAIExecutiveService.askGuardianUsingPost,
  });
  const started = useRef(false);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    if (request.kind === "question" && !started.current) {
      started.current = true;
      void mutateAsync({
        contextKey: context.key,
        contextKind: context.kind,
        label: context.label,
        metadata: context.metadata,
        query,
        promptId,
      }).then(
        reply => {
          if (mounted.current) complete(id, reply);
        },
        () => {
          if (mounted.current) complete(id);
        },
      );
    }
    return () => {
      mounted.current = false;
    };
  }, [id, request.kind, context, query, promptId, mutateAsync, complete]);
  useEffect(() => {
    if (request.kind !== "introduction") return;
    if (introduction.isSuccess) complete(id, introduction.data);
    else if (introduction.isError) complete(id);
  }, [
    id,
    request.kind,
    introduction.isSuccess,
    introduction.isError,
    introduction.data,
    complete,
  ]);
  return null;
};

const GuardianRuntime: React.FC<{
  entries: GuardianEntry[];
  complete: (id: string, reply?: GuardianScriptedReply) => void;
}> = ({ entries, complete }) => {
  return (
    <>
      {entries.map(item =>
        item.kind === "message" &&
        "thinking" in item.entry &&
        item.entry.thinking &&
        item.request ? (
          <Request
            key={item.id}
            id={item.id}
            request={item.request}
            complete={complete}
          />
        ) : null,
      )}
    </>
  );
};
export default GuardianRuntime;
